import React, { useState, useCallback, useEffect, useRef } from 'react';
import { forIn, mapValues } from 'lodash';
import { useDispatch } from 'react-redux';
import { Icon } from '@doxyme/ui';
import { localeService, components as Core } from '../../../core';
import { SurveyStep, SurveyTab, SurveyData, SurveyStarValue, RatingStars, SurveyTabs, } from '../../constants';
import { hideSurvey, hideSurveyDelayed, submitRating } from '../../actions';
import { ButtonType, Button } from '../../../../components/button';
import { Checkbox } from '../../../../components/checkbox';
import { CallSurveyHeader } from './CallSurveyHeader';
import { CallSurveyCloseButton } from './CallSurveyCloseButton';
import './CallQualitySurvey.scss';
export const CallQualitySurvey = () => {
    const dispatch = useDispatch();
    const [isSubmitDisabled, setSubmitStatus] = useState(true);
    const [starHoverValue, setStarHoverValue] = useState(0);
    const [selectedRatingValue, setRatingValue] = useState(0);
    const [currentTab, setCurrentTab] = useState(SurveyTab.Video);
    const [currentStep, setCurrentStep] = useState(SurveyStep.Start);
    const [surveyData, setSurveyData] = useState(SurveyData);
    const [comment, setComment] = useState('');
    const [focusedStarIndex, setFocusedStarIndex] = useState(0);
    const starRefs = useRef([]);
    const ratingStars = RatingStars;
    const tabs = SurveyTabs;
    const resultsMapper = useCallback(() => {
        const result = [];
        forIn(surveyData, (results) => {
            forIn(mapValues(results, 'value'), (val, problem) => {
                if (val) {
                    result.push(problem);
                }
            });
        });
        return result;
    }, [surveyData]);
    const handleStarClick = (value) => {
        setRatingValue(value);
        if (value === SurveyStarValue.Great) {
            setCurrentStep(SurveyStep.Final);
            const payload = {
                rating: value,
                data: resultsMapper(),
                comment,
                isProviderFeedback: true,
            };
            dispatch(submitRating(payload));
        }
        else {
            setCurrentStep(SurveyStep.Response);
        }
    };
    const handleCommentChange = (e) => {
        setComment(e.target.value);
    };
    const back = useCallback(() => {
        setSurveyData(SurveyData);
        setCurrentStep(SurveyStep.Start);
        setCurrentTab(SurveyTab.Video);
    }, []);
    useEffect(() => {
        setSubmitStatus(resultsMapper().length === 0 && comment.length === 0);
    }, [surveyData, comment, resultsMapper]);
    const handleCheckboxClick = (id, v) => {
        setSurveyData({
            ...surveyData,
            [currentTab]: {
                ...surveyData[currentTab],
                [id]: {
                    ...surveyData[currentTab][id],
                    value: !v,
                },
            },
        });
    };
    const hide = (shouldSubmit = false) => {
        dispatch(hideSurvey());
        if (shouldSubmit) {
            const payload = {
                rating: selectedRatingValue,
                data: [],
                comment: '',
                isProviderFeedback: true,
            };
            dispatch(submitRating(payload));
        }
    };
    const isValueGreaterThan = (value) => {
        return starHoverValue >= value || selectedRatingValue >= value;
    };
    const submit = useCallback(() => {
        setCurrentStep(SurveyStep.Final);
        dispatch(hideSurveyDelayed());
        const payload = {
            rating: selectedRatingValue,
            data: resultsMapper(),
            comment,
            isProviderFeedback: true,
        };
        dispatch(submitRating(payload));
    }, [dispatch, selectedRatingValue, resultsMapper, comment]);
    const focusStarAt = (index) => {
        const targetIndex = Math.max(0, Math.min(ratingStars.length - 1, index));
        setFocusedStarIndex(targetIndex);
        starRefs.current[targetIndex]?.focus();
    };
    const handleStarKeyDown = (event, index, value) => {
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                focusStarAt(index + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                focusStarAt(index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusStarAt(0);
                break;
            case 'End':
                event.preventDefault();
                focusStarAt(ratingStars.length - 1);
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                handleStarClick(value);
                break;
            default:
                break;
        }
    };
    const renderCurrentStep = () => {
        switch (currentStep) {
            case SurveyStep.Response:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.results" subtitle="call-quality-survey.select"/>
            <CallSurveyCloseButton onClick={() => hide(true)}/>
            <button className="call-quality-survey__back" onClick={back}>
              <Icon className="call-quality-survey__icon" name="chevron-left" size="x-small"/>
            </button>
            <div className="call-quality-survey__tabs-wrapper">
              <ul className="call-quality-survey__tabs">
                {tabs.map((tab, i) => (<li key={`${tab} + ${i}`} className={[
                            'call-quality-survey__tabs__tab',
                            'call-quality-survey__tab',
                            currentTab === tab ? 'active' : '',
                        ].join(' ')} onClick={() => setCurrentTab(tab)}>
                    <a>
                      {localeService.formatMessageById(`call-quality-survey.tab.${tab}`)}
                    </a>
                  </li>))}
                <li className="call-quality-survey__tabs__slider"/>
              </ul>
              <div className="call-quality-survey__tabs-content">
                {currentTab !== SurveyTab.Other
                        ? Object.keys(surveyData[currentTab]).map((option, i) => (<div className="call-quality-survey__tabs-content__rows" key={`${option} + ${i}`}>
                        <form name="form" role="form">
                          <Checkbox id={`active-${i}`} checked={surveyData[currentTab][option].value} label={localeService.formatMessageById(surveyData[currentTab][option].title)} onChange={() => handleCheckboxClick(option, surveyData[currentTab][option].value)}/>
                        </form>
                      </div>))
                        : null}
                {currentTab === SurveyTab.Other ? (<>
                    <textarea className="call-quality-survey__comment" value={comment} onChange={handleCommentChange} placeholder={localeService.formatMessageById('call-quality-survey.comment.placeholder')}/>
                    <p>
                      {localeService.formatMessageById('call-quality-survey.comment.PII-warning')}
                    </p>
                  </>) : null}
              </div>
              <div className="call-quality-survey__footer">
                <Button type={ButtonType.PRIMARY} onClick={submit} disabled={isSubmitDisabled}>
                  {localeService.formatMessageById('apps.action.submit')}
                </Button>
              </div>
            </div>
          </div>);
            case SurveyStep.Final:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.thanks" subtitle="call-quality-survey.good-day"/>
            <CallSurveyCloseButton onClick={hide}/>
            <div className="call-quality-survey-final__footer">
              <Button type={ButtonType.PRIMARY} onClick={hide}>
                {localeService.formatMessageById('apps.action.dismiss')}
              </Button>
            </div>
          </div>);
            default:
                return (<div>
            <CallSurveyHeader title="call-quality-survey.title" subtitle="call-quality-survey.subtitle"/>
            <CallSurveyCloseButton onClick={hide}/>
            <div role="radiogroup" className="call-quality-survey__rating" onMouseLeave={() => setStarHoverValue(0)}>
              {ratingStars.map((star, index) => (<div role="radio" tabIndex={index === focusedStarIndex ? 0 : -1} aria-checked={selectedRatingValue === star.value} aria-label={localeService.formatMessageById(star.label)} key={`star${star.value}`} ref={(el) => {
                            starRefs.current[index] = el;
                        }} className={[
                            'call-quality-survey__rating-item',
                            isValueGreaterThan(star.value)
                                ? 'call-quality-survey__rating-item--active'
                                : '',
                        ].join(' ')} onMouseEnter={() => setStarHoverValue(star.value)} onFocus={() => setFocusedStarIndex(index)} onKeyDown={(event) => handleStarKeyDown(event, index, star.value)} onClick={() => handleStarClick(star.value)}>
                  <Icon className="call-quality-survey__rating_ico" name="star"/>
                  <span className="call-quality-survey__rating_label">
                    <Core.RawHTML>
                      {localeService.formatMessageById(star.label)}
                    </Core.RawHTML>
                  </span>
                </div>))}
            </div>
          </div>);
        }
    };
    return (<div className={[
            'call-quality-survey__wrapper',
            currentStep === SurveyStep.Response ? 'full-height' : '',
        ].join(' ')}>
      {renderCurrentStep()}
    </div>);
};
